// src/components/Sidebar.jsx
import React, { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { 
  Home, 
  FileText, 
  Calendar, 
  ClipboardCheck, 
  Users, 
  User,
  Settings,
  ChevronDown,
  ChevronRight,
  LogOut,
  Menu,
  X,
  Ticket,
  ProjectorIcon
} from 'lucide-react';

const Sidebar = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState({
    appointments: false,
    medicalDetails: false,
    opdForms: false
  });
  
  const toggleExpand = (section) => {
    setExpanded({
      ...expanded,
      [section]: !expanded[section]
    });
  };

  const handleLogout = () => {
    logout();
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const getNavigationItems = () => {
    const items = [
      {
        name: 'Dashboard',
        icon: <Home size={20} />,
        path: '/dashboard',
        roles: ['ADMIN', 'EMP', 'INTERN', 'CLIENT'],
      },
      {
        name: 'All Bills',
        icon: <Calendar size={20} />,
        path: '/dashboard/bills',
        roles: ['ADMIN'],
      },
      {
        name: 'Ticket',
        icon: <Ticket size={20} />,
        path: '/dashboard/ticket',
        roles: ['ADMIN', 'EMP', 'INTERN', 'CLIENT',],
      },
      {
        name: 'Project',
        icon: <ProjectorIcon size={20} />,
        path: '/dashboard/project',
        roles: ['ADMIN', 'EMP', 'INTERN', 'CLIENT'],
      },

    ];

    // Add role-specific navigation items
    if (['ADMIN',].includes(user?.role)) {
      items.push({
        name: 'Client List',
        icon: <Calendar size={20} />,
        path: '/dashboard/client-list',
        roles: ['ADMIN'],
      });
      
      items.push({
        name: 'Employee List',
        icon: <Calendar size={20} />,
        path: '/dashboard/employee-list',
        roles: ['ADMIN'],
      });
      items.push({
        name: 'task-calendar',
        icon: <Calendar size={20} />,
        path: '/dashboard/task-calendar',
        roles: ['ADMIN'],
      });
      items.push({
        name: 'task-management',
        icon: <Calendar size={20} />,
        path: '/dashboard/task-management',
        roles: ['ADMIN'],
      });
      items.push({
        name: 'task',
        icon: <Calendar size={20} />,
        path: '/dashboard/task',
        roles: ['ADMIN'],
      });

    }



    // Add profile for all users
    items.push({
      name: 'Profile',
      icon: <User size={20} />,
      path: '/dashboard/profile',
      roles: ['CLIENT', 'EMP', 'INTERN', 'CEO', 'ADMIN', 'VAIBHAV'],
    });

    return items.filter(item => 
      item.roles && item.roles.includes(user?.role)
    );
  };

  const isActive = (path) => location.pathname === path;

  const navigation = getNavigationItems();

  return (
    <>
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-gray-900 text-white p-4">
        <button onClick={toggleSidebar} aria-label="Toggle Sidebar">
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <span>{user?.email}</span>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-10 md:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        ></div>
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-gray-900 text-white flex flex-col shadow-lg transform ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out z-20 md:translate-x-0 md:relative md:shadow-none`}
      >
        {/* Logo / Title */}
        <div className="p-5 border-b border-gray-800">
          <Link to="#" className="flex items-center">
            <img src='https://res.cloudinary.com/dnkx3dnnn/image/upload/v1730302177/kodekalp/e7r7rxqva0roc3dvypiu.png' alt="logo" className="h-10" />
          </Link>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-3">
            {navigation.map((item, index) => (
              <li key={index} className="mb-1">
                {item.expandable ? (
                  <div>
                    <button 
                      onClick={() => toggleExpand(item.section)}
                      className="flex items-center justify-between w-full p-3 rounded-md hover:bg-gray-800 transition-all text-gray-300"
                    >
                      <div className="flex items-center">
                        <span className="mr-3 text-gray-400">{item.icon}</span>
                        <span>{item.name}</span>
                      </div>
                      {expanded[item.section] ? 
                        <ChevronDown size={16} /> : 
                        <ChevronRight size={16} />
                      }
                    </button>
                    
                    {expanded[item.section] && (
                      <ul className="pl-10 mt-1 space-y-1">
                        {item.subItems.map((subItem, subIndex) => (
                          <li key={subIndex}>
                            <Link 
                              to={subItem.path}
                              className={`block p-2 rounded-md transition-all ${
                                isActive(subItem.path) 
                                  ? 'bg-blue-600 text-white' 
                                  : 'hover:bg-gray-800 text-gray-300'
                              }`}
                              onClick={() => setIsOpen(false)}
                            >
                              {subItem.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <Link 
                    to={item.path}
                    className={`flex items-center p-3 rounded-md transition-all ${
                      isActive(item.path) 
                        ? 'bg-blue-600 text-white' 
                        : 'hover:bg-gray-800 text-gray-300'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="mr-3 text-gray-400">{item.icon}</span>
                    <span>{item.name}</span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-800">
          <button
            onClick={() => {
              handleLogout();
              setIsOpen(false);
            }}
            className="flex items-center w-full p-2 rounded-md hover:bg-gray-800 transition-all text-gray-300"
            aria-label="Logout"
          >
            <LogOut size={20} className="mr-3" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;


// // src/components/Sidebar.jsx
// import React, { useContext, useState } from 'react';
// import { Link, useLocation } from 'react-router-dom';
// import { AuthContext } from '../context/AuthContext';
// import {
//   FaTachometerAlt,
//   FaCalendarAlt,
//   FaNotesMedical,
//   FaUserAlt,
//   FaSignOutAlt,
//   FaBars,
//   FaTimes,
//   FaClipboardCheck,
// } from 'react-icons/fa';

// const Sidebar = () => {
//   const { user, logout } = useContext(AuthContext);
//   const location = useLocation();
//   const [isOpen, setIsOpen] = useState(false);

//   const handleLogout = () => {
//     logout();
//   };

//   const toggleSidebar = () => {
//     setIsOpen(!isOpen);
//   };

//   const navigation = [
    
//     {
//       name: 'Dashboard',
//       path: '/dashboard',
//       icon: <FaTachometerAlt />,
//       roles: ['ADMIN', 'employee', 'intern', 'CLIENT'],
//     },
//     {
//       name: 'Appointment',
//       path: '/dashboard/appointment',
//       icon: <FaCalendarAlt />,
//       roles: ['patient', 'receptionist', 'doctor'],
//     },
//     {
//       name: 'Medical Details',
//       path: '/dashboard/medical-details',
//       icon: <FaNotesMedical />,
//       roles: ['patient'],
//     },{
//       name: 'All Patients',
//       path: '/dashboard/user-list',
//       icon: <FaClipboardCheck />,
//       roles: ['receptionist', 'doctor'],
//     },
//     {
//       name: 'Create OPD Form',
//       path: '/dashboard/opd-form',
//       icon: <FaNotesMedical />,
//       roles: ['receptionist', 'doctor'],
//     },
//     {
//       name: 'OPD List',
//       path: '/dashboard/opd-list',
//       icon: <FaClipboardCheck />,
//       roles: ['receptionist', 'doctor'],
//     },
    
//     {
//       name: 'Profile',
//       path: '/dashboard/profile',
//       icon: <FaUserAlt />,
//       roles: ['CLIENT', ],
//     },
    
//   ];

//   const filteredNavigation = navigation.filter((item) =>
//     item.roles.includes(user?.role)
//   );

//   const isActive = (path) => location.pathname === path;

//   return (
//     <>
//       {/* Mobile Header */}
//       <div className="md:hidden flex items-center justify-between bg-gray-900 text-white p-4">
//         <button onClick={toggleSidebar} aria-label="Toggle Sidebar">
//           {isOpen ? <FaTimes className="w-6 h-6" /> : <FaBars className="w-6 h-6" />}
//         </button>
//         <span>{ user.email}</span>
//       </div>

//       {/* Overlay */}
//       {isOpen && (
//         <div
//           className="fixed inset-0 bg-black opacity-50 z-10 md:hidden"
//           onClick={toggleSidebar}
//           aria-hidden="true"
//         > </div>
//       )}

//       {/* Sidebar */}
//       <div
//         className={`fixed top-0 left-0 h-full w-64 bg-gray-900 text-white flex flex-col shadow-lg transform ${
//           isOpen ? 'translate-x-0' : '-translate-x-full'
//         } transition-transform duration-300 ease-in-out z-20 md:translate-x-0 md:static md:shadow-none`}
//       >
//         {/* Logo / Title */}
//         <div className="p-6 text-2xl font-bold border-b border-gray-700">
//           <Link to="#" className="flex items-center">
//             {/* <FaTachometerAlt className="text-3xl" /> */}
//             {/* <span className="ml-3">Dashboard</span> */}
//             <img src='https://res.cloudinary.com/dnkx3dnnn/image/upload/v1730302177/kodekalp/e7r7rxqva0roc3dvypiu.png' alt="logo"/>
//           </Link>
//         </div>

//         {/* Navigation Links */}
//         <nav className="flex-1 p-6">
//           <ul className="space-y-2">
//             {filteredNavigation.map((item) => (
//               <li key={item.name}>
//                 <Link
//                   to={item.path}
//                   className={`flex items-center gap-3 p-2 text-base font-medium rounded-lg ${
//                     isActive(item.path)
//                       ? 'bg-gray-800 text-white'
//                       : 'text-gray-300 hover:bg-gray-800 hover:text-white'
//                   }`}
//                   onClick={() => setIsOpen(false)}
//                   aria-current={isActive(item.path) ? 'page' : undefined}
//                 >
//                   <span className="text-xl">{item.icon}</span>
//                   <span>{item.name}</span>
//                 </Link>
//               </li>
//             ))}
//           </ul>
//         </nav>

//         {/* Logout Button */}
//         <div className="p-6 border-t border-gray-700">
//           <button
//             onClick={() => {
//               handleLogout();
//               setIsOpen(false);
//             }}
//             className="w-full flex items-center gap-3 p-2 text-base font-medium rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white focus:outline-none"
//             aria-label="Logout"
//           >
//             <FaSignOutAlt className="text-xl" />
//             <span>Logout</span>
//           </button>
//         </div>
//       </div>
//     </>
//   );
// };

// export default Sidebar;


{
  // import React, { useState } from 'react';
  // import { Link, useLocation } from 'react-router-dom';
  // import { 
  //   Home, 
  //   FileText, 
  //   Briefcase, 
  //   Ticket, 
  //   CheckSquare, 
  //   Users, 
  //   CreditCard, 
  //   Bell, 
  //   Settings,
  //   ChevronDown,
  //   ChevronRight,
  //   LogOut
  // } from 'lucide-react';
  
  // const Sidebar = () => {
  //   const location = useLocation();
  //   const [expanded, setExpanded] = useState({
  //     projects: false,
  //     tickets: false,
  //     clients: false
  //   });
  
  //   const toggleExpand = (section) => {
  //     setExpanded({
  //       ...expanded,
  //       [section]: !expanded[section]
  //     });
  //   };
  
  //   const isActive = (path) => {
  //     return location.pathname === path;
  //   };
  
  //   const sidebarItems = [
  //     {
  //       name: 'Dashboard',
  //       icon: <Home size={20} />,
  //       path: '/',
  //     },
  //     {
  //       name: 'Projects',
  //       icon: <Briefcase size={20} />,
  //       expandable: true,
  //       section: 'projects',
  //       subItems: [
  //         { name: 'All Projects', path: '/projects' },
  //         { name: 'Active Projects', path: '/projects/active' },
  //         { name: 'Add New Project', path: '/projects/new' }
  //       ]
  //     },
  //     {
  //       name: 'Tickets',
  //       icon: <Ticket size={20} />,
  //       expandable: true,
  //       section: 'tickets',
  //       subItems: [
  //         { name: 'All Tickets', path: '/tickets' },
  //         { name: 'My Tickets', path: '/tickets/assigned' },
  //         { name: 'Open Tickets', path: '/tickets/open' }
  //       ]
  //     },
  //     {
  //       name: 'Tasks',
  //       icon: <CheckSquare size={20} />,
  //       path: '/tasks',
  //     },
  //     {
  //       name: 'Clients',
  //       icon: <Users size={20} />,
  //       expandable: true,
  //       section: 'clients',
  //       subItems: [
  //         { name: 'All Clients', path: '/clients' },
  //         { name: 'Add New Client', path: '/clients/new' }
  //       ]
  //     },
  //     {
  //       name: 'Billing',
  //       icon: <CreditCard size={20} />,
  //       path: '/billing',
  //     },
  //     {
  //       name: 'Notifications',
  //       icon: <Bell size={20} />,
  //       path: '/notifications',
  //     },
  //     {
  //       name: 'Settings',
  //       icon: <Settings size={20} />,
  //       path: '/settings',
  //     },
  //   ];
  
  //   return (
  //     <div className="h-screen bg-gray-900 text-white w-64 flex flex-col shadow-lg">
  //       <div className="p-5 border-b border-gray-800">
  //         <h1 className="text-xl font-bold">Project Manager</h1>
  //       </div>
        
  //       <div className="flex-1 overflow-y-auto py-4">
  //         <ul className="space-y-1 px-3">
  //           {sidebarItems.map((item, index) => (
  //             <li key={index}>
  //               {item.expandable ? (
  //                 <div>
  //                   <button 
  //                     onClick={() => toggleExpand(item.section)}
  //                     className="flex items-center justify-between w-full p-3 rounded-md hover:bg-gray-800 transition-all"
  //                   >
  //                     <div className="flex items-center">
  //                       <span className="mr-3 text-gray-400">{item.icon}</span>
  //                       <span>{item.name}</span>
  //                     </div>
  //                     {expanded[item.section] ? 
  //                       <ChevronDown size={16} /> : 
  //                       <ChevronRight size={16} />
  //                     }
  //                   </button>
                    
  //                   {expanded[item.section] && (
  //                     <ul className="pl-10 mt-1 space-y-1">
  //                       {item.subItems.map((subItem, subIndex) => (
  //                         <li key={subIndex}>
  //                           <Link 
  //                             to={subItem.path}
  //                             className={`block p-2 rounded-md transition-all ${
  //                               isActive(subItem.path) 
  //                                 ? 'bg-blue-600 text-white' 
  //                                 : 'hover:bg-gray-800 text-gray-300'
  //                             }`}
  //                           >
  //                             {subItem.name}
  //                           </Link>
  //                         </li>
  //                       ))}
  //                     </ul>
  //                   )}
  //                 </div>
  //               ) : (
  //                 <Link 
  //                   to={item.path}
  //                   className={`flex items-center p-3 rounded-md transition-all ${
  //                     isActive(item.path) 
  //                       ? 'bg-blue-600 text-white' 
  //                       : 'hover:bg-gray-800 text-gray-300'
  //                   }`}
  //                 >
  //                   <span className="mr-3 text-gray-400">{item.icon}</span>
  //                   <span>{item.name}</span>
  //                 </Link>
  //               )}
  //             </li>
  //           ))}
  //         </ul>
  //       </div>
        
  //       <div className="p-4 border-t border-gray-800">
  //         <button className="flex items-center w-full p-2 rounded-md hover:bg-gray-800 transition-all text-gray-300">
  //           <LogOut size={20} className="mr-3" />
  //           <span>Logout</span>
  //         </button>
  //       </div>
  //     </div>
  //   );
  // };
  
  // export default Sidebar;
}